import { useCallback } from 'react';
import { TranscriptPauseEntry } from './types';
import TranscriptPauseToolbar from './TranscriptPauseToolbar';
import useToolbarPosition from './useToolbarPosition';

interface TranscriptPauseBubbleProps {
  entry: TranscriptPauseEntry;
  onOptimize: (entry: TranscriptPauseEntry) => void;
  onRecover: (entry: TranscriptPauseEntry) => void;
  onOptimizeAll?: () => void;
  onRestoreAll?: () => void;
  hasDeleteAtPause: boolean;
  isHighlighted?: boolean;
}

export default function TranscriptPauseBubble({
  entry,
  onOptimize,
  onRecover,
  onOptimizeAll,
  onRestoreAll,
  hasDeleteAtPause,
  isHighlighted
}: TranscriptPauseBubbleProps) {
  const { showToolbar, setShowToolbar, closeToolbar, toolbarPosition, buttonRef, toolbarRef } = useToolbarPosition();

  const isOptimized = hasDeleteAtPause;
  const buttonClass = isOptimized
    ? 'bg-green-100 text-green-500 hover:bg-green-200'
    : 'bg-slate-200 text-slate-600 hover:bg-slate-300';

  const displayLabel = isOptimized ? `${entry.recommendedPause.toFixed(1)}s` : entry.label;

  const handleOptimize = useCallback((): void => {
    onOptimize(entry);
    closeToolbar();
  }, [onOptimize, entry, closeToolbar]);

  const handleRecover = useCallback((): void => {
    onRecover(entry);
    closeToolbar();
  }, [onRecover, entry, closeToolbar]);

  const handleOptimizeAll = useCallback((): void => {
    onOptimizeAll?.();
    closeToolbar();
  }, [onOptimizeAll, closeToolbar]);

  const handleRestoreAll = useCallback((): void => {
    onRestoreAll?.();
    closeToolbar();
  }, [onRestoreAll, closeToolbar]);

  return (
    <span className="relative ml-1 inline-flex">
      <button
        ref={buttonRef}
        type="button"
        data-testid="transcript-pause-entry"
        data-pause-type={entry.pauseType}
        className={`inline-flex cursor-pointer items-center rounded-md px-2 py-0 text-[13px] font-normal leading-[22px] transition ${isHighlighted ? '!bg-transcript-highlight-bg !text-transcript-highlight-text' : buttonClass}`}
        onClick={() => setShowToolbar(prev => !prev)}
      >
        <span>{displayLabel}</span>
      </button>

      {showToolbar && toolbarPosition && (
        <TranscriptPauseToolbar
          ref={toolbarRef}
          entry={entry}
          hasDelete={hasDeleteAtPause}
          position={toolbarPosition}
          onOptimize={handleOptimize}
          onRecover={handleRecover}
          onOptimizeAll={onOptimizeAll ? handleOptimizeAll : undefined}
          onRestoreAll={onRestoreAll ? handleRestoreAll : undefined}
        />
      )}
    </span>
  );
}
